import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Save, X, FileText, Package, Tag, Layers, 
  Calendar, User, Eye, AlertCircle, Link,
  ChevronLeft, ChevronRight, Maximize2, 
  SplitSquareVertical, EyeOff, Bold, Italic,
  List, ListOrdered, Link as LinkIcon,
  Image as ImageIcon, Code, Quote, HelpCircle
} from 'lucide-react';
import {
  Form, Input, Button, Card, Select, Typography, 
  Space, Divider, Tag as AntTag, Switch, Spin, 
  Alert, message, Upload, Row, Col, Badge, Modal
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import './KnowledgeBase.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Dragger } = Upload;

const ArticleEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [article, setArticle] = useState(null);
  const [editorMode, setEditorMode] = useState('split'); // 'edit', 'split', 'preview'
  const [content, setContent] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);

  // Filter options state
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    types: [],
    products: [],
    productCategories: [],
    statuses: ['draft', 'published', 'archived']
  });

  // Fetch article data
  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/kb-articles/${id}`);
      
      if (response.data.success) {
        const articleData = response.data.data;
        setArticle(articleData);
        setContent(articleData.content || '');
        updateCounts(articleData.content || '');
        
        // Set form values
        form.setFieldsValue({
          title: articleData.title,
          excerpt: articleData.excerpt,
          typeName: articleData.typeName || 'Knowledge Base Article',
          categoryName: articleData.categoryName,
          productName: articleData.productName,
          productCategoryName: articleData.productCategoryName,
          priorityId: articleData.priorityId,
          status: articleData.status || 'draft',
          tags: articleData.tags || []
        });
      } else {
        message.error('Article not found');
        navigate('/knowledge-base');
      }
    } catch (error) {
      console.error('Error fetching article:', error);
      message.error('Failed to load article');
      navigate('/knowledge-base');
    } finally {
      setLoading(false);
    }
  };

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/api/kb-articles/options/filters');
      
      if (response.data.success) {
        setFilterOptions({
          categories: response.data.data.categories || [],
          types: response.data.data.types || [],
          products: response.data.data.products || [],
          productCategories: response.data.data.productCategories || [],
          statuses: ['draft', 'published', 'archived']
        });
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
      message.warning('Could not load filter options');
    }
  };

  useEffect(() => {
    fetchArticle();
    fetchFilterOptions();
  }, [id]);

  // Update word and character counts
  const updateCounts = (text) => {
    const words = text.trim().split(/\s+/).filter(word => word.length > 0);
    setWordCount(words.length);
    setCharCount(text.length);
  };

  // Handle content change
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    updateCounts(newContent);
    form.setFieldsValue({ content: newContent });
  };

  // Handle editor mode change
  const handleEditorModeChange = (mode) => {
    setEditorMode(mode);
  };

  // Format text functions
  const formatText = (command, value = '') => {
    const textarea = document.getElementById('kb-content-textarea');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    let newContent = content;
    let newCursorPos = end;

    switch (command) {
      case 'bold':
        newContent = content.substring(0, start) + `**${selectedText}**` + content.substring(end);
        newCursorPos = start + selectedText.length + 4;
        break;
      case 'italic':
        newContent = content.substring(0, start) + `*${selectedText}*` + content.substring(end);
        newCursorPos = start + selectedText.length + 2;
        break;
      case 'link':
        const url = prompt('Enter URL:', 'https://');
        if (url) {
          const linkText = selectedText || 'link text';
          newContent = content.substring(0, start) + `[${linkText}](${url})` + content.substring(end);
          newCursorPos = start + linkText.length + url.length + 4;
        }
        break;
      case 'image':
        const imageUrl = prompt('Enter image URL:', 'https://');
        if (imageUrl) {
          const altText = prompt('Enter alt text:', '');
          newContent = content.substring(0, start) + `![${altText}](${imageUrl})` + content.substring(end);
          newCursorPos = start + altText.length + imageUrl.length + 5;
        }
        break;
      case 'code':
        newContent = content.substring(0, start) + '```\n' + selectedText + '\n```' + content.substring(end);
        newCursorPos = start + selectedText.length + 7;
        break;
      case 'quote':
        newContent = content.substring(0, start) + '> ' + selectedText + content.substring(end);
        newCursorPos = start + selectedText.length + 2;
        break;
      case 'list':
        newContent = content.substring(0, start) + '- ' + selectedText + content.substring(end);
        newCursorPos = start + selectedText.length + 2;
        break;
      case 'orderedList':
        newContent = content.substring(0, start) + '1. ' + selectedText + content.substring(end);
        newCursorPos = start + selectedText.length + 3;
        break;
      case 'header1':
        newContent = content.substring(0, start) + `# ${selectedText}` + content.substring(end);
        newCursorPos = start + selectedText.length + 2;
        break;
      case 'header2':
        newContent = content.substring(0, start) + `## ${selectedText}` + content.substring(end);
        newCursorPos = start + selectedText.length + 3;
        break;
      case 'header3':
        newContent = content.substring(0, start) + `### ${selectedText}` + content.substring(end);
        newCursorPos = start + selectedText.length + 4;
        break;
      default:
        return;
    }

    setContent(newContent);
    form.setFieldsValue({ content: newContent });
    updateCounts(newContent);

    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      
      const formData = {
        ...values,
        content: content,
        priorityId: values.priorityId || null,
        tags: values.tags || []
      };

      const response = await api.put(`/api/kb-articles/${id}`, formData);
      
      if (response.data.success) {
        message.success('Article updated successfully');
        navigate(`/knowledge-base/${id}`);
      } else {
        message.error(response.data.message || 'Failed to update article');
      }
    } catch (error) {
      console.error('Error updating article:', error);
      
      if (error.response) {
        const { status, data } = error.response;
        
        if (status === 404) {
          message.error('Article not found');
        } else if (status === 400) {
          message.error(data.message || 'Invalid data provided');
        } else {
          message.error(data.message || 'Failed to update article');
        }
      } else {
        message.error('Failed to update article. Please check your connection.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle save as draft
  const handleSaveDraft = () => {
    form.setFieldsValue({ status: 'draft' });
    form.submit();
  };

  // Handle publish
  const handlePublish = () => {
    form.setFieldsValue({ status: 'published' });
    form.submit();
  };

  // Handle cancel
  const handleCancel = () => {
    if (content !== article?.content || form.isFieldsTouched()) {
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to leave?',
        okText: 'Leave',
        cancelText: 'Stay',
        onOk: () => navigate(`/knowledge-base/${id}`)
      });
    } else {
      navigate(`/knowledge-base/${id}`);
    }
  };

  // Render markdown preview
  const renderMarkdownPreview = () => {
    if (!content) return <Text type="secondary">Start writing to see the preview...</Text>;

    // Basic markdown parsing
    const html = content
      .replace(/^# (.*$)/gm, '<h1>$1</h1>')
      .replace(/^## (.*$)/gm, '<h2>$1</h2>')
      .replace(/^### (.*$)/gm, '<h3>$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/!\[(.*?)\]\((.*?)\)/g, '<img src="$2" alt="$1" style="max-width: 100%; border-radius: 8px; margin: 1em 0;" />')
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
      .replace(/^\> (.*$)/gm, '<blockquote>$1</blockquote>')
      .replace(/^- (.*$)/gm, '<li>$1</li>')
      .replace(/^1\. (.*$)/gm, '<li>$1</li>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>')
      .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');

    return (
      <div className="markdown-body" dangerouslySetInnerHTML={{ __html: `<p>${html}</p>` }} />
    );
  };

  // Priority options
  const priorityOptions = [
    { value: 1, label: 'High', color: '#f56565' },
    { value: 2, label: 'Medium', color: '#ed8936' },
    { value: 3, label: 'Low', color: '#38a169' }
  ];

  if (loading) {
    return (
      <div className="kb-page">
        <div className="kb-header">
          <Title level={1} className="kb-title">Edit Article</Title>
          <Text className="kb-description">Loading article data...</Text>
        </div>
        <div className="kb-main-card">
          <div className="kb-content">
            <div className="kb-loading-container">
              <Spin size="large" />
              <div style={{ marginTop: '16px', color: '#718096' }}>Loading article...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="kb-page">
      {/* Header */}
      <div className="kb-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={1} className="kb-title">Edit Article</Title>
            <Text className="kb-description">
              Update and refine your knowledge base article
            </Text>
          </div>
          <Button
            icon={<ChevronLeft size={16} />}
            onClick={() => navigate(`/knowledge-base/${id}`)}
            className="kb-back-button"
            size="large"
          >
            Back to Article
          </Button>
        </div>
      </div>

      <div className="kb-main-card">
        <div className="kb-content">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              typeName: 'Knowledge Base Article',
              status: 'draft'
            }}
          >
            {/* Article Information Section */}
            <Card className="kb-form-section">
              <div className="kb-section-title">
                <FileText size={20} />
                Article Information
              </div>
              
              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <Form.Item
                    name="title"
                    label="Article Title"
                    rules={[{ required: true, message: 'Please enter article title' }]}
                  >
                    <Input 
                      placeholder="Enter article title" 
                      className="kb-form-input"
                      size="large"
                    />
                  </Form.Item>
                </Col>
                
                <Col span={24}>
                  <Form.Item
                    name="excerpt"
                    label="Article Excerpt"
                    help="Brief summary of the article (shown in listings)"
                  >
                    <TextArea 
                      placeholder="Enter article excerpt" 
                      className="kb-form-textarea"
                      rows={3}
                      showCount
                      maxLength={200}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Categorization Section */}
            <Card className="kb-form-section">
              <div className="kb-section-title">
                <Layers size={20} />
                Categorization
              </div>
              
              <Row gutter={[24, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="typeName"
                    label="Article Type"
                  >
                    <Select 
                      placeholder="Select article type"
                      className="kb-form-select"
                      size="large"
                      showSearch
                    >
                      <Option value="Knowledge Base Article">Knowledge Base Article</Option>
                      {filterOptions.types.map((type, index) => (
                        <Option key={type || `type-${index}`} value={type}>
                          {type}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                
                <Col xs={24} md={12}>
                  <Form.Item
                    name="categoryName"
                    label="Ticket Category"
                  >
                    <Select 
                      placeholder="Select ticket category"
                      className="kb-form-select"
                      size="large"
                      showSearch
                      allowClear
                    >
                      {filterOptions.categories.map((category, index) => (
                        <Option key={category || `cat-${index}`} value={category}>
                          {category}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                
                <Col xs={24} md={12}>
                  <Form.Item
                    name="productName"
                    label="Product"
                  >
                    <Select 
                      placeholder="Select product"
                      className="kb-form-select"
                      size="large"
                      showSearch
                      allowClear
                    >
                      {filterOptions.products.map((product, index) => (
                        <Option key={product || `prod-${index}`} value={product}>
                          {product}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                
                <Col xs={24} md={12}>
                  <Form.Item
                    name="productCategoryName"
                    label="Product Category"
                  >
                    <Select 
                      placeholder="Select product category"
                      className="kb-form-select"
                      size="large"
                      showSearch
                      allowClear
                    >
                      {filterOptions.productCategories.map((category, index) => (
                        <Option key={category || `pc-${index}`} value={category}>
                          {category}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                
                <Col xs={24} md={12}>
                  <Form.Item
                    name="priorityId"
                    label="Priority"
                  >
                    <Select 
                      placeholder="Select priority"
                      className="kb-form-select"
                      size="large"
                      allowClear
                    >
                      {priorityOptions.map(priority => (
                        <Option key={priority.value} value={priority.value}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: priority.color
                            }} />
                            <span>{priority.label}</span>
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                
                <Col xs={24} md={12}>
                  <Form.Item
                    name="status"
                    label="Article Status"
                  >
                    <Select 
                      className="kb-form-select"
                      size="large"
                    >
                      <Option value="draft">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FileText size={14} />
                          <span>Draft</span>
                        </div>
                      </Option>
                      <Option value="published">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Eye size={14} />
                          <span>Published</span>
                        </div>
                      </Option>
                      <Option value="archived">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <AlertCircle size={14} />
                          <span>Archived</span>
                        </div>
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Tags Section */}
            <Card className="kb-form-section">
              <div className="kb-section-title">
                <Tag size={20} />
                Tags & Keywords
              </div>
              
              <Form.Item
                name="tags"
                label="Article Tags"
                help="Add relevant tags for better searchability (press Enter after each tag)"
              >
                <Select
                  mode="tags"
                  placeholder="Enter tags and press Enter"
                  className="kb-form-select"
                  size="large"
                  tokenSeparators={[',']}
                />
              </Form.Item>
            </Card>

            {/* Content Editor Section */}
            <Card className="kb-form-section" style={{ padding: '0' }}>
              <div className="kb-editor-container">
                {/* Editor Header */}
                <div className="kb-editor-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div className="kb-editor-mode-toggle">
                      <Button
                        type={editorMode === 'edit' ? 'primary' : 'default'}
                        icon={<Maximize2 size={16} />}
                        onClick={() => handleEditorModeChange('edit')}
                        size="small"
                      >
                        Edit
                      </Button>
                      <Button
                        type={editorMode === 'split' ? 'primary' : 'default'}
                        icon={<SplitSquareVertical size={16} />}
                        onClick={() => handleEditorModeChange('split')}
                        size="small"
                      >
                        Split
                      </Button>
                      <Button
                        type={editorMode === 'preview' ? 'primary' : 'default'}
                        icon={<EyeOff size={16} />}
                        onClick={() => handleEditorModeChange('preview')}
                        size="small"
                      >
                        Preview
                      </Button>
                    </div>
                  </div>
                  
                  <div className="kb-editor-stats">
                    <div className="kb-stat-item">
                      <FileText size={12} />
                      {wordCount} words
                    </div>
                    <div className="kb-stat-item">
                      <Code size={12} />
                      {charCount} characters
                    </div>
                  </div>
                </div>

                {/* Editor Toolbar */}
                <div className="kb-editor-toolbar">
                  <Space size="small">
                    <Button
                      icon={<Bold size={14} />}
                      onClick={() => formatText('bold')}
                      className="kb-format-button"
                      title="Bold (Ctrl+B)"
                    />
                    <Button
                      icon={<Italic size={14} />}
                      onClick={() => formatText('italic')}
                      className="kb-format-button"
                      title="Italic (Ctrl+I)"
                    />
                    <Divider type="vertical" />
                    <Button
                      icon={<HelpCircle size={14} />}
                      onClick={() => formatText('header1')}
                      className="kb-format-button"
                      title="Heading 1"
                    />
                    <Button
                      icon={<HelpCircle size={14} />}
                      onClick={() => formatText('header2')}
                      className="kb-format-button"
                      title="Heading 2"
                    />
                    <Button
                      icon={<HelpCircle size={14} />}
                      onClick={() => formatText('header3')}
                      className="kb-format-button"
                      title="Heading 3"
                    />
                    <Divider type="vertical" />
                    <Button
                      icon={<List size={14} />}
                      onClick={() => formatText('list')}
                      className="kb-format-button"
                      title="Bullet List"
                    />
                    <Button
                      icon={<ListOrdered size={14} />}
                      onClick={() => formatText('orderedList')}
                      className="kb-format-button"
                      title="Numbered List"
                    />
                    <Divider type="vertical" />
                    <Button
                      icon={<LinkIcon size={14} />}
                      onClick={() => formatText('link')}
                      className="kb-format-button"
                      title="Insert Link"
                    />
                    <Button
                      icon={<ImageIcon size={14} />}
                      onClick={() => formatText('image')}
                      className="kb-format-button"
                      title="Insert Image"
                    />
                    <Button
                      icon={<Code size={14} />}
                      onClick={() => formatText('code')}
                      className="kb-format-button"
                      title="Code Block"
                    />
                    <Button
                      icon={<Quote size={14} />}
                      onClick={() => formatText('quote')}
                      className="kb-format-button"
                      title="Blockquote"
                    />
                  </Space>
                </div>

                {/* Editor Content */}
                <div className={`kb-editor-content ${editorMode}`}>
                  {/* Edit Mode - Full Width Editor */}
{editorMode !== 'preview' && (
  <div className={`kb-editor-textarea-container ${editorMode === 'edit' ? 'full' : 'split-left'}`}>
    <TextArea
      id="kb-content-textarea"
      value={content}
      onChange={handleContentChange}
      placeholder={`# Write your article here...

## Problem Description
Describe the issue or problem...

## Solution Steps
1. First step...
2. Second step...
3. Third step...

## Additional Notes
Any additional information or troubleshooting tips...

Use **bold**, *italic*, \`code\`, and [links](url) for formatting.`}
      rows={editorMode === 'split' ? 20 : 25}
      className="kb-content-textarea"
      autoSize={editorMode === 'split' ? { minRows: 20 } : { minRows: 25 }}
      spellCheck="true"
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
    />
  </div>
)}

                  {/* Preview Mode - Full Width Preview */}
                  {(editorMode === 'split' || editorMode === 'preview') && (
                    <div className={`kb-editor-preview-container ${editorMode === 'preview' ? 'full' : 'split-right'}`}>
                      <div className="kb-preview-header">
                        <Eye size={14} />
                        Preview
                      </div>
                      <div className="kb-preview-content">
                        {renderMarkdownPreview()}
                      </div>
                    </div>
                  )}
                </div>

                {/* Editor Footer */}
                <div className="kb-editor-footer">
                  <div className="kb-format-hint">
                    <span>Markdown Formatting:</span>
                    <span className="kb-hint-item">**bold**</span>
                    <span className="kb-hint-item">*italic*</span>
                    <span className="kb-hint-item"># Heading 1</span>
                    <span className="kb-hint-item">## Heading 2</span>
                    <span className="kb-hint-item">[link text](url)</span>
                    <span className="kb-hint-item">![alt text](image-url)</span>
                    <span className="kb-hint-item">- list item</span>
                    <span className="kb-hint-item">&gt; blockquote</span>
                    <span className="kb-hint-item">```code block```</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Linked Ticket Info (if available) */}
            {article?.ticketId && (
              <Card className="kb-form-section">
                <div className="kb-section-title">
                  <Link size={20} />
                  Linked Ticket Information
                </div>
                
                <Alert
                  type="info"
                  showIcon
                  message={
                    <div>
                      <strong>This article is linked to Ticket #{article.ticketId}</strong>
                      <div style={{ marginTop: '8px', fontSize: '14px' }}>
                        Category and product information has been automatically filled from the linked ticket.
                      </div>
                    </div>
                  }
                />
              </Card>
            )}

            {/* Form Actions */}
            <div className="kb-form-actions">
              <Button
                icon={<X size={16} />}
                onClick={handleCancel}
                className="kb-cancel-button"
                size="large"
                disabled={saving}
              >
                Cancel
              </Button>
              
              <Button
                icon={<Save size={16} />}
                onClick={handleSaveDraft}
                className="kb-draft-button"
                size="large"
                loading={saving}
                disabled={saving}
              >
                Save as Draft
              </Button>
              
              <Button
                type="primary"
                icon={<Eye size={16} />}
                onClick={handlePublish}
                className="kb-submit-button"
                size="large"
                loading={saving}
                disabled={saving}
              >
                Publish Article
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default ArticleEditPage;